import styled from "styled-components";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 640px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  justify-content: center;
`;

const Tab = styled.a`
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Timeline ── */
const ContentSection = styled.section`
  padding: clamp(40px, 5vw, 64px) 0 clamp(80px, 10vw, 120px);
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(28px, 3.5vw, 48px);
`;

const YearBlock = styled.section`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: clamp(28px, 4vw, 56px);
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const YearTag = styled.div`
  font-size: clamp(56px, 7vw, 88px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
  padding: 4px 8px 4px 0;
  width: fit-content;
  background: linear-gradient(135deg, #6366f1, #7c3aed);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: sticky;
  top: 100px;
  height: fit-content;

  @media (max-width: 760px) {
    position: static;
    font-size: 56px;
  }
`;

const Items = styled.ul`
  list-style: none;
  padding: 0 0 0 28px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  border-left: 2px solid #eef2ff;
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 16px;
  align-items: baseline;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: -35px;
    top: 9px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid #6366f1;
    box-shadow: 0 0 0 4px #ffffff;
  }

  .month {
    font-size: 12.5px;
    font-weight: 600;
    color: #6366f1;
    background: #eef2ff;
    border-radius: 6px;
    padding: 4px 8px;
    text-align: center;
    letter-spacing: 0.02em;
  }

  .text {
    font-size: 15px;
    line-height: 1.65;
    color: #334155;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 6px;

    .month {
      width: fit-content;
    }
  }
`;

const HISTORY = [
  {
    year: "2025",
    items: [
      { month: "05", text: "Seed funding round closed" },
      { month: "04", text: "MOU signed with SingHealth Polyclinics" },
      { month: "03", text: "Incorporation (corporate entity established)" },
    ],
  },
  {
    year: "2024",
    items: [
      {
        month: "10",
        text: "Selected for FreeTips program (Korea Startup & Entrepreneurship Promotion Agency)",
      },
      {
        month: "06",
        text: "Selected as a 2024 Global Accelerating Startup Company (National Sports Corporation)",
      },
      {
        month: "05",
        text: "Selected as a demand agency for SMB cloud service expansion (Korea Small Business Technology Innovation Association)",
      },
      {
        month: "03",
        text: "Selected as a First Penguin company (Credit Guarantee Fund)",
      },
    ],
  },
  {
    year: "2023",
    items: [
      {
        month: "07",
        text: "Selected as a small and medium-sized smart service introduction company (Korea Small Business Technology Information Promotion Agency)",
      },
      {
        month: "06",
        text: "2023 AI (prototype) product and service production support (Artificial Intelligence Industry Convergence Business Group)",
      },
      {
        month: "05",
        text: "2nd Digital Health Demand-Supply Company Matching Day (Korea Digital Health Industry Association)",
      },
      {
        month: "05",
        text: "Data voucher (AI processing) demand company (Korea Data Industry Promotion Agency)",
      },
      {
        month: "05",
        text: "R&D Stepping Stone for start-up growth technology development (Gwangju Creation Innovation Center)",
      },
      {
        month: "05",
        text: "K-CAMP Gwangju 4th cohort (Korea Securities Depository)",
      },
      {
        month: "04",
        text: "Start-up NEST 13th cohort (Credit Guarantee Fund)",
      },
      {
        month: "04",
        text: "Startup Success Package (Gwangju Youth Startup Academy)",
      },
    ],
  },
  {
    year: "2022",
    items: [
      {
        month: "11",
        text: "Preliminary start-up package specialization (IR competency enhancement program)",
      },
      {
        month: "10",
        text: "Cognitive Health Cohort Multimodal Data Application Demonstration Base Construction Project (Gwangju Techno Park)",
      },
      {
        month: "09",
        text: "IP Stepping Stone (Korea Invention Promotion Association)",
      },
      {
        month: "06",
        text: "Integrated IR to stimulate investment by local companies in future industries",
      },
      { month: "06", text: "Youngand established" },
      {
        month: "05",
        text: "Specialized field of pre-startup package (Korea Startup & Entrepreneurship Promotion Agency)",
      },
      {
        month: "02",
        text: "Gwangju Special Zone Accelerator Expansion Project (Gwangju Techno Park)",
      },
    ],
  },
];

const TABS = [
  { key: "greetings", label: "Greetings", href: "/aboutUs/greetings" },
  { key: "vision", label: "Vision", href: "/aboutUs/vision" },
  { key: "history", label: "History", href: "/aboutUs/history" },
  { key: "map", label: "Map", href: "/aboutUs/map" },
];

export function History() {
  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Company</Eyebrow>
          <HeroTitle>History</HeroTitle>
          <HeroLead>
            Milestones on Youngand&apos;s journey — programs, partnerships,
            and recognition that shaped where we are today.
          </HeroLead>
          <Tabs>
            {TABS.map((t) => (
              <Tab key={t.key} href={t.href} $active={t.key === "history"}>
                {t.label}
              </Tab>
            ))}
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <Timeline>
            {HISTORY.map((y) => (
              <YearBlock key={y.year}>
                <YearTag>{y.year}</YearTag>
                <Items>
                  {y.items.map((it, idx) => (
                    <Item key={idx}>
                      <span className="month">{it.month}</span>
                      <span className="text">{it.text}</span>
                    </Item>
                  ))}
                </Items>
              </YearBlock>
            ))}
          </Timeline>
        </Container>
      </ContentSection>
    </Page>
  );
}
